import { useEffect, useState } from 'react';
import StatsCardSection from '../components/StatsCardSection';
import LeadsDailyChart from '../components/LeadsDailyChart';
import MonthComparisonCard from '../components/MonthComparisonCard';
import DonutChart from '../components/DonutChart';
import FunnelPipeline from '../components/FunnelPipeline';
import { getConsumePlan, getAgentCostRanking } from '../services/donutChartApi';
import { getDashboardLeadStats, type DashboardLeadStats } from '../services/leadStatsApi';
import { Loader2, ChevronDown } from 'lucide-react';

interface CostRankingItem {
    name: string;
    conversations: number;
    estimatedCost: string;
    usageTotal?: number;
    usageDaily?: number;
}

export default function Dashboard() {
    const [leadStats, setLeadStats] = useState<DashboardLeadStats | null>(null);
    const [consumePlan, setConsumePlan] = useState<{ percentage: number; used: number; limit: number }>({ percentage: 0, used: 0, limit: 10000 });
    const [costRanking, setCostRanking] = useState<CostRankingItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedAgent, setSelectedAgent] = useState<string | null>(null);

    useEffect(() => {
        Promise.all([
            getDashboardLeadStats().then(setLeadStats),
            getConsumePlan().then(setConsumePlan),
            getAgentCostRanking().then(setCostRanking),
        ]).finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-[#F5F5F7]">
                <Loader2 className="h-6 w-6 animate-spin text-[#1A4D2E]" />
            </div>
        );
    }

    const agents = leadStats?.agents || [];
    const totalSpend = costRanking.reduce((sum, a) => sum + (a.usageTotal || 0), 0);

    // Calculate filtered stats
    let todayLeads: number;
    let yesterdayLeads: number;
    let totalLeads: number;

    if (selectedAgent && leadStats?.stats_by_agent) {
        const agentStat = leadStats.stats_by_agent.find(s => s.agent_id === selectedAgent);
        todayLeads = agentStat?.today || 0;
        yesterdayLeads = agentStat?.yesterday || 0;
        totalLeads = agentStat?.total || 0;
    } else {
        todayLeads = leadStats?.today_leads || 0;
        yesterdayLeads = leadStats?.yesterday_leads || 0;
        totalLeads = leadStats?.total_leads || 0;
    }

    return (
        <div className="min-h-screen bg-[#F5F5F7] p-6 md:p-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
                <div className="relative">
                    <select
                        value={selectedAgent || ''}
                        onChange={(e) => setSelectedAgent(e.target.value || null)}
                        className="appearance-none bg-white border border-gray-200 rounded-xl px-4 py-2 pr-9 text-sm font-medium text-gray-700 shadow-sm hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/20 focus:border-[#1A4D2E] cursor-pointer transition-colors"
                    >
                        <option value="">Todos los agentes</option>
                        {agents.map(agent => (
                            <option key={agent.id} value={agent.id}>{agent.name}</option>
                        ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
            </div>

            {/* Bento Grid */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                {/* ROW 1: Stats (left 9col) + Chart (right 3col, spans 2 rows) */}
                <div className="md:col-span-9">
                    <StatsCardSection
                        todayLeads={todayLeads}
                        yesterdayLeads={yesterdayLeads}
                        totalLeads={totalLeads}
                        totalSpend={totalSpend}
                    />
                </div>

                <div className="md:col-span-3 md:row-span-2">
                    <LeadsDailyChart
                        dailyData={leadStats?.daily_leads_by_agent || []}
                        monthlyData={leadStats?.monthly_chart_by_agent || []}
                        selectedAgent={selectedAgent}
                    />
                </div>

                {/* ROW 2: Month comparison (left 5col) + Donut (right 4col) */}
                <div className="md:col-span-5">
                    <MonthComparisonCard
                        data={leadStats?.monthly_by_agent || []}
                        selectedAgent={selectedAgent}
                    />
                </div>
                <div className="md:col-span-4">
                    <DonutChart
                        used={consumePlan.used}
                        limit={consumePlan.limit}
                        percentage={consumePlan.percentage}
                        baseColor="#00FF7A"
                    />
                </div>

                {/* ROW 3: Pipeline full width */}
                <div className="md:col-span-12">
                    <FunnelPipeline
                        data={leadStats?.leads_by_stage || []}
                        selectedAgent={selectedAgent}
                    />
                </div>

                {/* ROW 4: OpenRouter Ranking full width */}
                <div className="md:col-span-12">
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between mb-5">
                            <div>
                                <h3 className="text-sm font-semibold text-gray-900">Consumo OpenRouter</h3>
                                <p className="text-[11px] text-gray-400 mt-0.5">Datos reales de uso &middot; Este mes</p>
                            </div>
                            <span className="text-lg font-bold text-gray-900">${totalSpend.toFixed(2)}</span>
                        </div>
                        {costRanking.length === 0 ? (
                            <p className="text-xs text-gray-400 text-center py-6">Sin actividad este mes</p>
                        ) : (
                            <div className="space-y-3">
                                {costRanking.map((agent, i) => {
                                    const maxCost = parseFloat(costRanking[0]?.estimatedCost || '1') || 1;
                                    const thisCost = parseFloat(agent.estimatedCost) || 0;
                                    return (
                                        <div key={agent.name} className="flex items-center gap-3 group">
                                            <span className="w-5 h-5 rounded-full bg-[#1A4D2E] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                                                {i + 1}
                                            </span>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className="text-xs font-medium text-gray-700 truncate">{agent.name}</span>
                                                    <span className="text-xs font-bold text-gray-900 flex-shrink-0 tabular-nums">${agent.estimatedCost}</span>
                                                </div>
                                                <div className="w-full bg-gray-100 rounded-full h-1.5">
                                                    <div
                                                        className="h-1.5 rounded-full transition-all duration-500"
                                                        style={{
                                                            width: `${Math.min((thisCost / maxCost) * 100, 100)}%`,
                                                            background: 'linear-gradient(90deg, #00FF7A, #1A4D2E)',
                                                        }}
                                                    />
                                                </div>
                                                {agent.usageDaily !== undefined && (
                                                    <p className="text-[10px] text-gray-400 mt-0.5">
                                                        Hoy: ${agent.usageDaily.toFixed(4)} &middot; Total: ${(agent.usageTotal || 0).toFixed(2)}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
